/**
 * Perioada abonamentului: **început + număr de luni → ultima zi a lunii.**
 *
 * ```
 * expire_date = ultima zi a lunii (start_date + N luni)
 * ```
 *
 * | Început | Luni | Expirare |
 * |---|---|---|
 * | 15.06.2026 | 2 | 31.08.2026 |
 * | 31.01.2026 | 1 | 28.02.2026 |
 * | 01.09.2026 | 2 | 30.11.2026 |
 *
 * ⚠️ **Nu sunt N luni de serviciu.** 15 iunie + 2 luni ține până la 31 august, adică două
 * luni și jumătate. E intenționat — luna se dă întreagă. Scris aici pentru că e exact genul
 * de lucru pe care cineva îl „repară" peste un an, crezând că e un bug de calcul.
 *
 * ## De ce e partajată
 *
 * Data asta se tipărește pe o anexă pe care clientul o semnează. Formularul o arată înainte
 * de salvare, serverul o scrie în coloană — două implementări ar fi divergent într-o zi, iar
 * cea greșită ar fi deja pe hârtie. Aici e una singură, iar contractul primește `months`, nu
 * data gata calculată.
 *
 * ## Fără `Date`
 *
 * `new Date('2026-08-18')` e miezul nopții **UTC**, adică 3 dimineața la București; un
 * `toISOString()` peste el, în cealaltă direcție, dă ziua precedentă. O dată calendaristică
 * nu e un moment în timp. Aici se lucrează pe cifre și șiruri, ca la sume.
 *
 * Vezi docs/wiki/domains/subscriptions.md
 */

/** Sub o lună nu se vinde. Serverul refuză, formularul nu lasă. */
export const MIN_MONTHS = 1;

/** Ce scrie în câmp la deschiderea unui abonament nou (fondatorul, 2026-08-19). */
export const DEFAULT_MONTHS = 2;

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

/** An bisect după regula gregoriană întreagă: 2000 e bisect, 1900 nu. */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** Câte zile are luna. `month` e 1–12. */
export function daysInMonth(year: number, month: number): number {
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }
  return DAYS_IN_MONTH[month - 1] ?? 0;
}

interface CalendarDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

/**
 * `value` e o zi care chiar există în calendar?
 *
 * Regexul singur nu ajunge: `2026-02-30` are forma cerută și n-a existat niciodată. Cu
 * `new Date` ar fi devenit tăcut 2 martie; aici e respinsă la graniță.
 */
export function isCalendarDate(value: string): boolean {
  return parseDate(value) !== null;
}

/** `YYYY-MM-DD` → cifre, sau `null` dacă nu e o zi care există în calendar. */
function parseDate(value: string): CalendarDate | null {
  const match = DATE_PATTERN.exec(value.trim());

  if (!match) {
    return null;
  }

  const [, yearText = '', monthText = '', dayText = ''] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);

  // 30 februarie trece de regex și e totuși o dată care nu există. Refuzată aici, nu
  // rotunjită tăcut la 1 martie, cum ar fi făcut `new Date`.
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    return null;
  }

  return { year, month, day };
}

function toIsoDate({ year, month, day }: CalendarDate): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * `YYYY-MM-DD` → `19.08.2026`, forma în care se citește o dată pe un act românesc
 * ([#164](https://github.com/bplugaru/efx-pcrm/issues/164)).
 *
 * Perechea lui `toIsoDate`, în cealaltă direcție: în bază, în API și în formulare data
 * rămâne ISO; forma asta e **numai pentru tipărit și afișat**.
 *
 * ⚠️ **Ce nu e o zi din calendar se întoarce neschimbat** — nu gol, nu `null`. Textul ăsta
 * ajunge pe un contract: o valoare nerecunoscută trebuie să se vadă la corectură, iar un
 * `null` ar fi fost mai rău decât inutil — cheia ar fi lipsit din context, iar
 * `resolveHoles` ar fi lăsat în act **eticheta din editor**, adică „Document — dată" tipărit
 * pe hârtia semnată.
 *
 * ⚠️ **Nu se compară niciodată două date în forma asta.** `2026-08-19 < 2026-09-01` e
 * adevărat ca șiruri; `19.08.2026 < 01.09.2026` e fals. Comparațiile rămân pe ISO.
 */
export function formatCalendarDate(value: string): string {
  const parsed = parseDate(value);

  if (parsed === null) {
    return value;
  }

  const { year, month, day } = parsed;

  return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${String(year).padStart(4, '0')}`;
}

/**
 * Data de expirare: **ultima zi a lunii în care cade `startDate + months`.**
 *
 * `null` dacă începutul nu e o dată validă sau dacă `months` nu e un întreg ≥ 1 — apelantul
 * decide ce face cu asta: serverul respinge cererea, formularul nu arată nicio dată.
 */
export function expireDateFor(startDate: string, months: number): string | null {
  const start = parseDate(startDate);

  if (!start || !Number.isInteger(months) || months < MIN_MONTHS) {
    return null;
  }

  // Lunile numărate de la zero, ca să treacă peste an fără cazuri speciale: decembrie + 2
  // e luna 13, adică februarie anul următor.
  const shifted = start.month - 1 + months;
  const year = start.year + Math.floor(shifted / 12);
  const month = (shifted % 12) + 1;

  // Ziua din `startDate` nu intră în calcul: luna se dă întreagă, deci ce contează e luna
  // în care cade, nu ziua din ea.
  return toIsoDate({ year, month, day: daysInMonth(year, month) });
}

/**
 * Câte luni s-au dat, citite dintr-un abonament salvat.
 *
 * Diferența e **de luni calendaristice**, nu de zile: e drumul invers al lui
 * `expireDateFor`, care ignoră ziua. Pe un rând scris de noi, `expireDateFor(start, months)`
 * dă înapoi exact `expire`.
 *
 * ⚠️ Pe rândurile vechi (seed, import din sistemul actual) expirarea poate să **nu** fie
 * ultima zi a lunii. Numărul întors e tot corect ca durată, dar `expireDateFor` peste el dă
 * altă dată decât cea stocată — iar formularul trebuie s-o spună, nu s-o corecteze tăcut.
 *
 * `null` dacă datele nu sunt valide sau dacă expirarea e în aceeași lună cu începutul ori
 * înaintea lui: sub o lună nu e o durată pe care s-o poată reprezenta câmpul.
 */
export function monthsBetween(startDate: string, expireDate: string): number | null {
  const start = parseDate(startDate);
  const expire = parseDate(expireDate);

  if (!start || !expire) {
    return null;
  }

  const months = (expire.year - start.year) * 12 + (expire.month - start.month);

  return months < MIN_MONTHS ? null : months;
}

/**
 * Ziua de azi, `YYYY-MM-DD`, în calendarul de pe ecran.
 *
 * Nu `toISOString().slice(0, 10)`: pe 1 septembrie la 01:00, ora României, ăla dă 31 august.
 * `now` se dă din afară ca ziua să poată fi fixată în teste.
 *
 * Partajată pentru că o cer amândouă capetele: formularul prepopulează începutul
 * abonamentului cu ea, iar serverul o folosește ca prag — o dată de reactivare în trecut e
 * refuzată, nu scrisă într-un rând de istoric care nu se mai corectează.
 */
export function today(now: Date = new Date()): string {
  return toIsoDate({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  });
}
